import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAllCalibrationSets, skipCalibration } from "../config/calibration";

const initialState = {
	calibrationSets: null, // contains all calibration sets from the server
	questions: [], // contains 15 questions that have been displayed
	chosenQuestionSetId: [], //contains the first question ID of each question set
	chosenCalibrationId: [], //{calibrationId, numberOfQuestions}
	seenQuestions: 0,
	curElo: null,
	status: 'idle',
	error: null,
}

export const getCalibrationSets = createAsyncThunk(
	"getCalibration/getAllCalibrationSets",
	async (_, thunkAPI) => {
		try {
			const { result } = await getAllCalibrationSets();
			return result;
		} catch (error) {
			return thunkAPI.rejectWithValue(error.response?.data?.errorMessage || "Unknown error");
		}
	}
);

const calibrationSlice = createSlice({
	name: "calibration",
	initialState,
	reducers: {
		setStatus: (state, action) => {
			state.status = action.payload;
		},
		setSeenQuestions: (state, action) => {
			state.seenQuestions = action.payload;
		},
		setQuestions: (state, action) => {
			for (let i = 0; i < action.payload.length; i++) {
				action.payload[i].questionIndex = state.questions.length + i;
			}
			state.questions = state.questions.concat(action.payload);
		},
		setCurElo: (state, action) => {
			state.curElo = action.payload;
		},
		setCorrectQuestion: (state, action) => {
			// {questionIndex: number, correct: boolean}
			if (!state.questions || !Array.isArray(state.questions) || action.payload.questionIndex >= state.questions.length) {
				return;
			}
			state.questions[action.payload.questionIndex].isCorrect = action.payload.correct;
		},
		setQuestionFinished: (state, action) => {
      // {questionIndex: number}
			if (!state.questions || !Array.isArray(state.questions) || action.payload >= state.questions.length) {
        return;
      }
      state.questions[action.payload].isFinished = true;
    },
		setQuestionState: (state, action) => {
      // {questionState: Object}
      if (!state.questions || !Array.isArray(state.questions) || action.payload.index >= state.questions.length) {
        return;
      }
      state.questions[action.payload.index].question.state = action.payload.state;
    },
		setQuestionAnswer: (state, action) => {
			state.questions[action.payload.questionState.index].answer = action.answer
		},
		setQuestionVisible: (state, action) => {
			if (!state.questions || !Array.isArray(state.questions) || action.payload >= state.questions.length) {
        return;
      }
      if (state.questions[action.payload - 1]?.isLast) {
        return;
      }
      state.questions[action.payload].visible = true;
    },
		setChosenQuestionId: (state, action) => {
			state.chosenQuestionSetId = [...state.chosenQuestionSetId, action.payload]
		},
		setChosenCalibrationId: (state, action) => {
			state.chosenCalibrationId = [...state.chosenCalibrationId, action.payload]
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(getCalibrationSets.pending, (state) => {
				state.status = "loading";
			})
			.addCase(getCalibrationSets.fulfilled, (state, action) => {
				state.status = "succeeded";
				state.calibrationSets = action.payload;
			})
			.addCase(getCalibrationSets.rejected, (state, action) => {
				state.status = "failed";
				state.error = action.payload || "Failed to fetch calibration sets";
			})
	},
});

export const {
	setStatus,
	setSeenQuestions, 
	setQuestions, 
	setCurElo,
	setCorrectQuestion,
	setQuestionFinished,
	setQuestionState,
	setQuestionAnswer,
	setQuestionVisible,
	setChosenQuestionId,
	setChosenCalibrationId
} = calibrationSlice.actions;

export default calibrationSlice.reducer;
